import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Field, Panel, SectionTitle, TextArea } from '../../../../components';
import { firstErrorMessage, firstWarningMessage, issuesFor } from '../../issues-by-field';
import {
  resetSiteField,
  resetSiteText,
  resolveRouteKeywords,
  setSiteAi,
  setSiteImage,
  setSiteKeywords,
  setSiteText,
} from '../../seo-document';
import type { SiteSeoDocument, SiteSeoIssue } from '../../seo-types';
import { DESCRIPTION_MAX, DESCRIPTION_MIN } from '../../seo-validation';
import { applyTitleTemplate, templateHasPageToken } from '../../title-template';
import { AiAssistantsPanel } from '../AiAssistantsPanel';
import { FieldTag, type FieldTagKind } from '../FieldTag';
import { GenerateHost } from '../GenerateWithLumio';
import { KeywordsField } from '../KeywordsField';
import { ShareImageField } from '../SharePanel';
import { Body, ErrorText, FieldBlock, HintText, Note, TagRow } from './SiteDefaultsPanel.styles';

export type SiteDefaultsPanelProps = {
  draft: SiteSeoDocument;
  language: string;
  defaultLanguage: string;
  /** The site's host (the share image and footer panels of Tasks 6-7 read it). */
  siteHost: string;
  /** The server's issues; each field shows its first site-level error as is. */
  issues: SiteSeoIssue[];
  /** The brand's tenant: its website art is the share image library. */
  tenantId?: number;
  /** Stored with an uploaded share image. */
  brandName: string;
  /** The server's llms.txt as of its last GET or draft PUT answer. */
  llmsPreview: string;
  onChange: (update: (draft: SiteSeoDocument) => SiteSeoDocument) => void;
  /** The AI assistants panel's Lumio generate control. */
  aiGenerateSlot?: React.ReactNode;
  /** Lumio writing into the AI assistants panel, and the fields still holding what it wrote. */
  aiGenerating?: { busy: boolean; generated: readonly string[] };
  /** Lumio writing into the Search result panel (the site defaults bundle), and the fields still holding what it
   *  wrote. */
  searchGenerating?: { busy: boolean; generated: readonly string[] };
};

type SiteField = 'title_template' | 'description';

/** The live example's page: a sample page label (data, like the site's own labels), so it is not a catalog key that
 *  would also translate every "Slots" label the app renders. */
const EXAMPLE_PAGE = 'Slots';

const NOTHING_GENERATED: readonly string[] = [];

/**
 * Site defaults, Search result part: the title template (with a live example, and a hint when it lacks `{page}`) and
 * the default description (with its counter), then the default keywords (context Lumio gets for every page, never a
 * page's focus keyword, so never scored here). A field the owner never edited is absent from the document and follows
 * the platform ("Platform default"); an edited one says so and offers "Reset to platform default", which drops it.
 * Then Social share: the default share image every route without its own uses; then AI assistants (robots switches,
 * the llms.txt summary and key facts, the server's llms.txt preview).
 */
export const SiteDefaultsPanel = ({
  draft,
  language,
  defaultLanguage,
  issues,
  tenantId,
  brandName,
  llmsPreview,
  onChange,
  aiGenerateSlot,
  aiGenerating,
  searchGenerating,
}: SiteDefaultsPanelProps): React.ReactElement => {
  const { t } = useTranslation();
  const { site } = draft;

  const own = (field: SiteField): string => site[field]?.[language] ?? '';
  const fallback = (field: SiteField): string =>
    language === defaultLanguage ? '' : site[field]?.[defaultLanguage] ?? '';
  const tagOf = (field: SiteField): FieldTagKind => {
    if (site[field] === undefined) return 'platform-default';
    if (own(field) === '' && fallback(field) !== '') return 'using-default-language';
    return 'edited';
  };
  const set = (field: SiteField, value: string): void => onChange((d) => setSiteText(d, field, language, value));
  // On another language than the default, Reset drops only that language's text, and only once it has one.
  const reset = (field: SiteField): void => onChange((d) => resetSiteText(d, field, language, defaultLanguage));
  const resettable = (field: SiteField): boolean =>
    site[field] !== undefined && (language === defaultLanguage || own(field) !== '');

  const block = (field: SiteField, control: React.ReactNode, extra: React.ReactNode, warn: boolean) => {
    const fieldIssues = issuesFor(issues, null, field, language);
    const error = firstErrorMessage(fieldIssues);
    const warning = error ? undefined : firstWarningMessage(fieldIssues);
    const tone = warn || warning !== undefined;
    return (
      <FieldBlock data-field={field} data-tone={tone ? 'warning' : undefined} $warn={tone}>
        {control}
        <TagRow>
          <FieldTag kind={tagOf(field)} language={defaultLanguage} />
          {resettable(field) ? (
            <Button label={t('Reset to platform default')} variant="text" onClick={() => reset(field)} />
          ) : null}
        </TagRow>
        {extra}
        {error ? <ErrorText text={error} raw tag="span" /> : null}
        {warning ? <HintText text={warning} raw tag="span" /> : null}
      </FieldBlock>
    );
  };

  const template = own('title_template') || fallback('title_template');
  const description = own('description');
  const descriptionWarn =
    description !== '' && (description.length < DESCRIPTION_MIN || description.length > DESCRIPTION_MAX);

  const ownKeywords = site.keywords?.[language] ?? [];
  const resolvedKeywords = resolveRouteKeywords(draft, null, language, defaultLanguage);
  const inheritedKeywords = resolvedKeywords.source === 'default-language' ? resolvedKeywords.keywords : undefined;
  const keywordsTag: FieldTagKind =
    site.keywords === undefined ? 'platform-default' : inheritedKeywords ? 'using-default-language' : 'edited';

  const shareImage = site.share_image;
  const shareError = firstErrorMessage(issuesFor(issues, null, 'share_image', language));

  return (
    <>
      <GenerateHost
        kind="search"
        busy={searchGenerating?.busy ?? false}
        generated={searchGenerating?.generated ?? NOTHING_GENERATED}
      >
        <Panel>
          <SectionTitle title="Search result" />
          <Body>
            {block(
              'title_template',
              <Field
                label={t('Title template')}
                value={own('title_template')}
                onChange={(value) => set('title_template', value)}
                placeholder={fallback('title_template') || t('{page} · Your brand')}
              />,
              template ? (
                <>
                  <Note
                    text="Example: {{example}}"
                    placeholders={{ example: applyTitleTemplate(template, EXAMPLE_PAGE) }}
                    tag="span"
                  />
                  {!templateHasPageToken(template) ? (
                    <HintText text="Add {page} where the page name goes" tag="span" />
                  ) : null}
                </>
              ) : null,
              template !== '' && !templateHasPageToken(template),
            )}
            {block(
              'description',
              <TextArea
                label={t('Default description')}
                value={description}
                onChange={(value) => set('description', value)}
                placeholder={fallback('description') || undefined}
                hint={`${description.length}/${DESCRIPTION_MAX}`}
                rows={3}
              />,
              null,
              descriptionWarn,
            )}
            <FieldBlock data-field="keywords" $warn={false}>
              <KeywordsField
                label="Default keywords"
                placeholder="Add a keyword"
                value={ownKeywords}
                inherited={inheritedKeywords}
                onChange={(next) => onChange((d) => setSiteKeywords(d, language, next))}
              />
              <TagRow>
                <FieldTag kind={keywordsTag} language={defaultLanguage} />
              </TagRow>
              <Note text="Context for Lumio on every page, never a page's focus keyword." tag="span" />
            </FieldBlock>
          </Body>
        </Panel>
      </GenerateHost>
      <Panel>
        <SectionTitle title="Social share" />
        <Body>
          <FieldBlock data-field="share_image" $warn={false}>
            <Note text="Default share image" tag="span" />
            <ShareImageField
              shown={shareImage}
              inherited={false}
              allowSiteDefault={false}
              tenantId={tenantId}
              brandName={brandName}
              onPick={(image) => onChange((d) => setSiteImage(d, image))}
            />
            <TagRow>
              <FieldTag kind={shareImage === undefined ? 'platform-default' : 'edited'} />
              {shareImage !== undefined ? (
                <Button
                  label={t('Reset to platform default')}
                  variant="text"
                  onClick={() => onChange((d) => resetSiteField(d, 'share_image'))}
                />
              ) : null}
            </TagRow>
            {shareError ? <ErrorText text={shareError} raw tag="span" /> : null}
          </FieldBlock>
        </Body>
      </Panel>
      <GenerateHost
        kind="ai"
        busy={aiGenerating?.busy ?? false}
        generated={aiGenerating?.generated ?? NOTHING_GENERATED}
      >
        <AiAssistantsPanel
          ai={site.ai}
          llmsPreview={llmsPreview}
          issues={issues}
          onChange={(patch) => onChange((d) => setSiteAi(d, patch))}
          generateSlot={aiGenerateSlot}
        />
      </GenerateHost>
    </>
  );
};

export default SiteDefaultsPanel;
